'use client';

import { styled } from 'styled-components';
import TextLink from '@dvd/components/TextLink';
import Media from '@dvd/components/Media';
import cs from 'classnames';
import type { IconNameType } from '@wistia/ui';
import FeatureCard from '@dvd/components/FeatureCard';
import * as Styled from '@lib/styles';
import { CtaProps, ImageProps, SafeUrl } from '@types';

type FeatureProps = Record<
  string,
  {
    headline: string;
    description: string;
    href: SafeUrl;
    icon: IconNameType;
  }
>;

export const FeaturesLibrary = {
  hosting: {
    headline: 'Hosting',
    description:
      'Your home for top-notch video playback, analytics, and organization.',
    href: '/product/hosting',
    icon: 'home',
  },
  interactive_video: {
    headline: 'Engagement',
    description: 'Key features that turn passive viewers into active ones.',
    href: '/product/interactive-video',
    icon: 'integrations',
  },
  record: {
    headline: 'Recording',
    description: 'Create super polished videos with just your laptop.',
    href: '/product/video-recording',
    icon: 'record',
  },
  video_management: {
    headline: 'Management',
    description: 'Store, organize, find, and archive your videos in a jiffy.',
    href: '/product/video-management',
    icon: 'project',
  },
  collections: {
    headline: 'Collections',
    description: 'Display your video collections just the way you want',
    href: '/product/collections',
    icon: 'channel',
  },
  player: {
    headline: 'Playback',
    description:
      'We designed our player to be fast, customizable, and compatible with any browser or device.',
    href: '/product/player',
    icon: 'play',
  },
  channels: {
    headline: 'Video galleries',
    description:
      'Build an on-brand page for all your content. No coding required.',
    href: '/product/channels',
    icon: 'channel',
  },
  wistia_forms: {
    headline: 'Wistia forms',
    description: 'Capture leads with branded, in-video forms.',
    href: '/product/wistia-forms',
    icon: 'private-user-sessions',
  },
  live_streaming: {
    headline: 'Webinars',
    description:
      'Hosting webinars is now a piece of cake. Preferably red velvet.',
    href: '/product/webinar-hosting',
    icon: 'live',
  },
  analytics: {
    headline: 'Video analytics',
    description:
      'Get nerdy with key metrics, heatmaps, and A/B testing at your fingertips.',
    href: '/product/analytics',
    icon: 'stats',
  },
  podcasting: {
    headline: 'Podcasting',
    description:
      'Upload your pod and we’ll distribute it to top directories like Apple and Spotify.',
    href: '/product/podcasting',
    icon: 'mic',
  },
  video_seo: {
    headline: 'SEO',
    description:
      'We automatically optimize your videos for search to get you more views.',
    href: '/product/video-seo',
    icon: 'search',
  },
  marketing_automation: {
    headline: 'Marketing integrations',
    description:
      'Automatically send video data to your favorite marketing tools.',
    href: '/product/marketing-automation',
    icon: 'integrations',
  },
  video_editing: {
    headline: 'Editing',
    description: 'Zhuzhing up your videos has never been easier.',
    href: '/product/video-editing',
    icon: 'scissors',
  },
  collaboration: {
    headline: 'Collaboration',
    description: 'We’ve made getting feedback simple and fun. Really!',
    href: '/product/collaboration',
    icon: 'thumbs-up',
  },
  embeds: {
    headline: 'Embedding',
    description:
      'Copy and paste our embed code onto your page for reliable playback on any device.',
    href: '/product/embeds',
    icon: 'media',
  },
  zoom: {
    headline: 'Zoom integration',
    description:
      'Zoom videos auto-import into Wistia as soon as you finish recording.',
    href: '/integrations/zoom',
    icon: 'integrations',
  },
  youtube: {
    headline: 'YouTube integration',
    description:
      'Combine Wistia’s world-class hosting with YouTube’s discoverability and reach.',
    href: '/integrations/youtube',
    icon: 'integrations',
  },
  platform: {
    headline: 'Wistia API',
    description: 'Build custom video experiences for your site.',
    href: '/product/platform',
    icon: 'embed',
  },
  mcp_server: {
    headline: 'Wistia MCP',
    description: 'Run Wistia from agents like Claude, Cursor, and ChatGPT.',
    href: '/product/mcp-server',
    icon: 'file-tree',
  },
  localization: {
    headline: 'Localization',
    description: 'AI dubbing and translations in 50+ languages.',
    href: '/product/localize',
    icon: 'localization',
  },
  accessibility: {
    headline: 'Accessibility',
    description: 'Everything you need to create an inclusive video experience.',
    href: '/product/accessibility',
    icon: 'accessibility',
  },
} satisfies FeatureProps;

type Feature = keyof typeof FeaturesLibrary;

type FeatureListProps = {
  /**
   * Eyebrow sub-headline
   */
  eyebrowText?: string;
  /**
   * What the H3 element says
   */
  headline: string;
  /**
   * Descriptive paragraph
   */
  description: string;
  /**
   * Which FeatureCards do you want to display? (Requires at least 7.)
   */
  features: [
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    ...Feature[],
  ];
  /**
   * Color variant of the feature cards. `light` (default) suits light
   * sections; `dark` renders navy cards with white text for dark sections
   * (e.g. a `blueDark` `Section`).
   */
  variant?: 'light' | 'dark';
  /**
   * Optional CTA
   */
  cta?: CtaProps;
  /**
   * The hashed ID of the video displayed left of the features grid
   * - **Required** for video content (mutually exclusive with image)
   */
  hashedId?: string;
  /**
   * Featured image to displayed left of the features grid
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   */
  image?: ImageProps;
};

const FeatureListContainer = styled.div`
  margin: ${({ theme }) => `${theme.spacing[2]} 0`};

  .textLink {
    display: block;
    text-align: center;
    margin-top: ${({ theme }) => theme.spacing[2]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: ${({ theme }) => `${theme.spacing[4]} 0`};
  }
`;

const FeatureListHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;

const FeatureListGrid = styled.div`
  display: grid;
  margin-block: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => theme.grid.columns.small};

  .featureMedia {
    display: none;
  }

  .featureCards {
    display: grid;
    grid-column: auto / span 2;
    grid-template-columns: subgrid;
    row-gap: ${({ theme }) => theme.spacing[2]};
    align-items: start;

    & > * {
      grid-column: auto / span 2;
    }
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    ${({ theme }) => theme.grid.columns.medium};
    .featureCards {
      grid-column: auto / span 6;

      & > * {
        grid-column: auto / span 3;
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block: ${({ theme }) => theme.spacing[3]};

    ${({ theme }) => theme.grid.columns.large};
    .featureCards {
      grid-column: auto / span 12;

      & > * {
        grid-column: auto / span 6;
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.xl} {
    margin-block: ${({ theme }) => theme.spacing[4]};

    .featureCards {
      grid-column: auto / span 12;

      & > * {
        grid-column: auto / span 4;
      }
    }

    &.withMedia {
      .featureMedia {
        display: block;
        grid-column: auto / span 4;
        margin: 0;
      }

      .featureCards {
        align-self: start;
        grid-column: auto / span 8;
      }
    }
  }
`;

const FeatureList = ({
  cta = null,
  description,
  eyebrowText,
  features,
  headline,
  hashedId,
  image,
  variant = 'light',
}: FeatureListProps) => {
  const hasMedia = Boolean(hashedId || image);

  return (
    <FeatureListContainer>
      <FeatureListHeader>
        {eyebrowText ? <Styled.Eyebrow>{eyebrowText}</Styled.Eyebrow> : null}
        <h2>{headline}</h2>
        <p>{description}</p>
      </FeatureListHeader>
      <FeatureListGrid className={cs(hasMedia ? 'withMedia' : null)}>
        {hasMedia ? (
          <div className="featureMedia">
            {hashedId ? <Media hashedId={hashedId} /> : null}
            {image?.src ? (
              <Media
                alt={image.alt}
                height={image.height}
                src={image.src}
                width={image.width}
              />
            ) : null}
          </div>
        ) : null}
        <div className="featureCards">
          {features.map((title) => (
            <FeatureCard
              key={title}
              size="sd"
              variant={variant}
              trackingLabel={title}
              trackingProperty="feature-list"
              {...FeaturesLibrary[title]}
            />
          ))}
        </div>
      </FeatureListGrid>
      {cta ? <TextLink className="textLink" cta={cta} /> : null}
    </FeatureListContainer>
  );
};

export default FeatureList;
